/* summary.js — Génère une phrase de synthèse par fichier depuis le modèle
 * normalisé. Chaînes de texte regroupées ici. Expose self.MediaSummary. */
'use strict';

var NUM_M = ['zéro', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf', 'dix'];
// `fem` met « une » au lieu de « un »
function word(n, fem) {
  if (n == null) return '';
  if (n === 1 && fem) return 'une';
  if (n >= 0 && n <= 10) return NUM_M[n];
  return String(n);
}
function plural(n, sing, plur) { return n > 1 ? plur : sing; }

// Noms courts ; le modèle garde le nom long.
var CONTAINER_SHORT = {
  'Matroska': 'MKV', 'WebM': 'WebM', 'MPEG-4': 'MP4', 'QuickTime': 'MOV',
  'Wave': 'WAV', 'AVI': 'AVI', 'MPEG-TS': 'MPEG-TS', 'BDAV': 'M2TS',
  'FLAC': 'FLAC', 'MPEG Audio': 'MP3', 'Ogg': 'OGG'
};
function shortContainer(c) {
  if (!c || c.formatRaw == null) return null;
  return CONTAINER_SHORT[c.formatRaw] || c.formatRaw;
}

// Noms courts ; le modèle garde l'alias complet.
var VCODEC_SHORT = { 'AVC': 'H.264', 'HEVC': 'HEVC', 'MPEG Video': 'MPEG-2', 'VP9': 'VP9', 'VP8': 'VP8', 'AV1': 'AV1' };
var ACODEC_SHORT = { 'AC-3': 'AC-3', 'E-AC-3': 'E-AC-3', 'MLP FBA': 'TrueHD', 'MPEG Audio': 'MP3' };
function shortVCodec(raw) { return raw ? (VCODEC_SHORT[raw] || raw) : null; }
function shortACodec(raw) { return raw ? (ACODEC_SHORT[raw] || raw) : null; }

function shortHDR(h) {
  if (!h) return null;
  if (/dolby\s*vision/i.test(h)) return 'Dolby Vision';
  if (/hdr10\+/i.test(h)) return 'HDR10+';
  if (/hdr10/i.test(h)) return 'HDR10';
  if (/hlg/i.test(h)) return 'HLG';
  return 'HDR';
}

// Langues connues d'un ensemble de pistes, sans doublons.
function langNames(tracks) {
  var seen = {}, out = [];
  tracks.forEach(function (t) {
    var n = t.language && t.language.name;
    if (n && !seen[n]) { seen[n] = 1; out.push(n.toLowerCase()); }
  });
  return out;
}

function videoClause(v) {
  var s = 'une vidéo';
  var codec = shortVCodec(v.formatRaw);
  if (codec) s += ' ' + codec;
  if (v.bitDepth) s += ' ' + v.bitDepth + ' bits';
  var hdr = shortHDR(v.hdrFormat);
  if (hdr) s += ' ' + hdr;
  if (v.scanType === 'interlaced') s += ' entrelacée';
  if (v.width && v.height) s += ' en ' + v.width + ' × ' + v.height;
  return s;
}

function audioDesc(a) {
  var bits = [];
  var codec = shortACodec(a.formatRaw);
  if (codec) bits.push(codec);
  if (a.channelLayout && a.channelLayout.friendly) bits.push(a.channelLayout.friendly);
  var s = bits.join(' ');
  if (a.language && a.language.name) s += (s ? ' ' : '') + 'en ' + a.language.name.toLowerCase();
  return s.trim();
}

function audioClause(auds) {
  var n = auds.length;
  if (n === 1) {
    var d = audioDesc(auds[0]);
    return 'une piste audio' + (d ? ' ' + d : '');
  }
  var langs = langNames(auds);
  var s = word(n) + ' pistes audio';
  if (langs.length) s += ' (' + langs.join(', ') + ')';
  return s;
}

function subClause(subs) {
  var n = subs.length;
  var langs = langNames(subs);
  var s = word(n) + ' ' + plural(n, 'sous-titre', 'sous-titres');
  if (langs.length) s += ' (' + langs.join(', ') + ')';
  return s;
}

function finish(s) {
  if (!s) return '';
  s = s.charAt(0).toUpperCase() + s.slice(1);
  if (!/[.!?]$/.test(s)) s += '.';
  return s;
}

function describe(model) {
  var c = model.container;
  var vids = [], auds = [], subs = [], imgs = [];
  model.tracks.forEach(function (t) {
    if (t.kind === 'video') vids.push(t);
    else if (t.kind === 'audio') auds.push(t);
    else if (t.kind === 'text') subs.push(t);
    else if (t.kind === 'image') imgs.push(t);
  });

  if (!vids.length && !auds.length) {
    if (imgs.length) {
      var im = imgs[0];
      var dim = (im.width && im.height) ? ' en ' + im.width + ' × ' + im.height : '';
      return finish('image ' + (im.formatRaw || '') + dim);
    }
    return 'Fichier non reconnu ou sans piste média analysable.';
  }

  var cont = shortContainer(c);
  var parts = [];

  if (vids.length) {
    var lead = (cont ? cont + ' contenant ' : 'Fichier contenant ') + videoClause(vids[0]);
    if (vids.length > 1) lead += ' (+ ' + word(vids.length - 1) + ' ' + plural(vids.length - 1, 'autre piste vidéo', 'autres pistes vidéo') + ')';
    parts.push(lead);
    if (auds.length) parts.push(audioClause(auds));
    if (subs.length) parts.push(subClause(subs));
  } else {
    var head = cont ? 'fichier audio ' + cont + ' : ' : 'fichier audio : ';
    // audio seul : la première piste est décrite en détail
    var first = audioDesc(auds[0]);
    var sr = auds[0].samplingRate ? ', ' + (auds[0].samplingRate / 1000).toLocaleString('fr-FR') + ' kHz' : '';
    var bd = auds[0].bitDepth ? ', ' + auds[0].bitDepth + ' bits' : '';
    var body = (first || 'audio') + sr + bd;
    if (auds.length > 1) body += ' (+ ' + word(auds.length - 1) + ' ' + plural(auds.length - 1, 'autre piste', 'autres pistes') + ')';
    parts.push(head + body);
    if (subs.length) parts.push(subClause(subs));
  }

  return finish(parts.join(', '));
}

var api = { describe: describe };
globalThis.MediaSummary = api;
export default api;
